import type { CampaignTerminalsResponse } from "components/Form/formComponents/TerminalToCampaignTable/types";
import i18n from "i18next";
import { type Dispatch, type FC, type SetStateAction, useEffect, useState } from "react";
import styled from "styled-components";

import { CAMPAIGN_STATE } from "src/app/features/Campaign/types/CampaignState";
import type { TerminalToCampaignEntity } from "src/app/features/Campaign/types/TerminalToCampaignEntity";
import { Palette } from "../../core/styles/palette";

export interface Segment {
    percentage: number;
    count: number;
    color: string;
    label: string;
}

interface MultiSegmentProgressBarProps {
    value?: TerminalToCampaignEntity[];
    data?: CampaignTerminalsResponse;
    setTerminals: Dispatch<SetStateAction<TerminalToCampaignEntity[]>>;
    refreshIndex: number;
}

export const MultiSegmentProgressBar: FC<MultiSegmentProgressBarProps> = ({ value, data, setTerminals, refreshIndex }) => {
    const [segments, setSegments] = useState<Segment[]>([]);

    useEffect(() => {
        if (!value?.length || !data?.items) {
            setSegments([]);
            return;
        }

        const phaseTerminalIds = new Set(value.map((terminal) => terminal.terminal));
        const phaseTerminals = data.items.filter((terminal) => phaseTerminalIds.has(terminal.terminal));
        setTerminals(phaseTerminals);

        const total = phaseTerminals.length;
        const updated = phaseTerminals.filter((item) => item.updateStatus === CAMPAIGN_STATE.UPDATED);
        const failed = phaseTerminals.filter((item) => item.updateStatus === CAMPAIGN_STATE.FAILED);
        const unfinished = phaseTerminals.filter(
            (item) => item.updateStatus !== CAMPAIGN_STATE.FAILED && item.updateStatus !== CAMPAIGN_STATE.UPDATED,
        );

        setSegments([
            {
                percentage: total === 0 ? 0 : (updated.length / total) * 100,
                count: updated.length,
                color: Palette.HeroGreen,
                label: i18n.t("campaignCreate.phase.progress.updated"),
            },
            {
                percentage: total === 0 ? 0 : (unfinished.length / total) * 100,
                count: unfinished.length,
                color: Palette.Warning,
                label: i18n.t("campaignCreate.phase.progress.unfinished"),
            },
            {
                percentage: total === 0 ? 0 : (failed.length / total) * 100,
                count: failed.length,
                color: Palette.AeviRed,
                label: i18n.t("campaignCreate.phase.progress.failed"),
            },
        ]);
    }, [value, data, setTerminals, refreshIndex]);

    return (
        <div style={{ marginTop: 16 }}>
            <BarContainerStyled>
                {segments.map((seg) => (
                    <ProgressBarStyled
                        state={seg}
                        key={`${seg.label}-${seg.color}`}
                        aria-valuenow={seg.percentage}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        role="progressbar"
                    >
                        {seg.count > 0 && (
                            <span>
                                {seg.label} {seg.percentage.toFixed(2)}% ({seg.count})
                            </span>
                        )}
                    </ProgressBarStyled>
                ))}
            </BarContainerStyled>
        </div>
    );
};

const BarContainerStyled = styled.div`
    display: flex;
    height: 24px;
    border-radius: 20px;
    overflow: hidden;
    background: ${Palette.DisabledBackground};
`;

const ProgressBarStyled = styled.div<{ state: Segment }>`
    width: ${(props) => props.state.percentage}%;
    background: ${(props) => props.state.color};
    display: ${(props) => (props.state.percentage > 0 ? "flex" : "none")};
    align-items: center;
    justify-content: center;
    color: ${Palette.White};
    font-weight: 500;
    font-size: 12px;
`;
